import React from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { C } from "../common/theme";

export interface PageTab {
  value: string;
  label: React.ReactNode;
  /** Small count shown after the label (hidden when null/undefined) */
  count?: number | null;
}

interface PageShellProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Buttons on the right of the title; they wrap under it on phones */
  actions?: React.ReactNode;
  tabs?: { value: string; onChange: (value: string) => void; items: PageTab[] };
  children: React.ReactNode;
  /** Max content width in px (default 1320) */
  maxWidth?: number;
}

/** The frame every page uses: title, subtitle, actions, optional underlined
 * tabs, then the content in one column with even gaps. The window scrolls;
 * pages never set their own heights or overflow. */
export default function PageShell({ title, subtitle, actions, tabs, children, maxWidth = 1320 }: PageShellProps) {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth,
        mx: "auto",
        px: { xs: 2, md: 3, lg: 4 },
        pt: { xs: 2, md: 3 },
        pb: { xs: 6, md: 6 },
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: { xs: 2, md: 2.5 },
        minWidth: 0,
      }}
    >
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1.5, justifyContent: "space-between" }}>
        <Box sx={{ minWidth: 0, flex: "1 1 280px" }}>
          <Typography variant="h4" component="h1">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: C.muted, mt: 0.5, maxWidth: 760 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", "& > *": { flex: { xs: "1 1 auto", sm: "0 0 auto" } } }}>
            {actions}
          </Box>
        )}
      </Box>
      {tabs && (
        <Tabs
          value={tabs.value}
          onChange={(_, value) => tabs.onChange(value)}
          variant="scrollable"
          scrollButtons={false}
          sx={{ mt: -0.5 }}
        >
          {tabs.items.map((item) => (
            <Tab
              key={item.value}
              value={item.value}
              label={
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                  {item.label}
                  {item.count !== undefined && item.count !== null && (
                    <Box
                      component="span"
                      sx={{ fontSize: 11.5, fontWeight: 700, px: 0.75, py: 0.1, borderRadius: "5px", bgcolor: tabs.value === item.value ? C.ink : C.line, color: tabs.value === item.value ? "#fff" : C.ink2 }}
                    >
                      {item.count}
                    </Box>
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
      )}
      {children}
    </Box>
  );
}

/** A titled block inside a page. */
export function Section({
  title,
  subtitle,
  actions,
  children,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Box component="section" sx={{ display: "flex", flexDirection: "column", gap: 1.25, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, flexWrap: "wrap", justifyContent: "space-between" }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="h2">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: C.muted }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions}
      </Box>
      {children}
    </Box>
  );
}

/** A white bordered box, the default container for content. */
export function Panel({ children, padding = 2, sx }: { children: React.ReactNode; padding?: number; sx?: object }) {
  return (
    <Box sx={{ bgcolor: C.paper, border: `1px solid ${C.line}`, borderRadius: "8px", p: padding, minWidth: 0, ...sx }}>
      {children}
    </Box>
  );
}
